import React from 'react';

import http from 'api/http';
import { boatAPI } from 'api/requests';

import { MultiInput, Input } from 'Components/Input';
import ApplicationActions from 'Components/ApplicationActions';
import { CalculatedMultiOutput, CalculatedOutput } from 'Components/CalculatedOutput';
import NotificationManager from 'dom-managers/notification';
import { BOAT, INBOARD, OUTBOARD, PONTOON, PWC, TWIN } from 'constants/boatApplication';
import Slider from 'Components/Slider';
import legacyTagImage from '../../../../../images/type-1.png';
import modernTagImage from '../../../../../images/type-4.png';
import { isTagTypeSelectionLengthEligible } from './personPowerCapacityEligibility';

export default class PersonPowerCapacity extends React.Component {
  constructor(props) {
    super(props);

    const personPowerCapacity = props.boat.personPowerCapacity || {};
    const persistedPlateType = props.boat.plateType || personPowerCapacity.plateType || '';
    const hasModernPlateType = /(?:PLATE_TYPE_|Plate type )(?:[4-9]|10)$/i.test(persistedPlateType);
    const hasSpecifiedEngineWeight = Number(personPowerCapacity.reqSpecifiedEngineWeight) > 0;
    const hasElectricPropulsion = props.boat.engineType === OUTBOARD &&
      props.boat.boatSpecifications && props.boat.boatSpecifications.hasElectricPropulsion;
    const persistedUseManufacturerEngineWeight = personPowerCapacity.useManufacturerEngineWeight !== undefined
      ? personPowerCapacity.useManufacturerEngineWeight
      : hasSpecifiedEngineWeight;

    this.state = {
      ...personPowerCapacity,
      preferredTagType: hasElectricPropulsion
        ? 'MODERN'
        : personPowerCapacity.preferredTagType || (hasModernPlateType ? 'MODERN' : 'LEGACY'),
      useManufacturerEngineWeight: hasElectricPropulsion ? true : persistedUseManufacturerEngineWeight,
      reqSpecifiedEngineWeight: hasElectricPropulsion && persistedUseManufacturerEngineWeight === false
        ? ''
        : personPowerCapacity.reqSpecifiedEngineWeight
    };

    this.recalculationPending = false;
    this.inputErrors = {};
  }

  componentDidMount() {
    if (this.props.stepIsChanged) this.calculateData();
  }

  calculateData = () => {
    const {id} = this.props.boat;
    const showSpecifiedEngineWeight = this.shouldShowSpecifiedEngineWeight();
    const {
      calBoatWeight,
      plateType,
      reqMaxHP,
      reqMaxWeight,
      reqMaxNumberOfPersons,
      reqMaxPersonsWeight,
      reqMaxHPDualSteering,
      reqSpecifiedEngineWeight,
      preferredTagType,
      useManufacturerEngineWeight,
      apprMaxHP,
      appMaxWeight,
      appMaxNumberOfPersons,
      appMaxPersonsWeight,
      apprMaxHPDualSteering,
      overrideApprovedValues
    } = this.state;

    return new Promise((resolve, reject) => {
      http
        .put(boatAPI.calculatePersonWeightCapacity(id), {
          reqMaxHP,
          reqMaxWeight,
          reqMaxNumberOfPersons,
          reqMaxPersonsWeight,
          reqMaxHPDualSteering,
          reqSpecifiedEngineWeight: showSpecifiedEngineWeight ? (reqSpecifiedEngineWeight || 0) : 0,
          preferredTagType,
          useManufacturerEngineWeight,
          overrideApprovedValues,
        })
        .then(({ data }) => {
          let newState = this.state;

          if (!this.isEmptyInputs()) {
            for (let stateFieldName in this.state) {
              if (this.state[stateFieldName] !== undefined && data[stateFieldName] !== undefined) {
                newState[stateFieldName] = data[stateFieldName] > 0 ? data[stateFieldName] : 0;
                delete this.inputErrors[stateFieldName];
              }
            }
            if (apprMaxHP !== undefined)
              newState.apprMaxHP = this.choseApprovedValue(newState.calcMaxHP, this.state.reqMaxHP);
            if (appMaxWeight !== undefined)
              newState.appMaxWeight = this.choseApprovedValue(newState.calMaxWeight, this.state.reqMaxWeight);
            if (appMaxNumberOfPersons !== undefined)
              newState.appMaxNumberOfPersons = this.choseApprovedValue(
                newState.calMaxNumberOfPersons,
                this.state.reqMaxNumberOfPersons
              );
            if (appMaxPersonsWeight !== undefined)
              newState.appMaxPersonsWeight = this.choseApprovedValue(
                newState.calMaxPersonsWeight,
                this.state.reqMaxPersonsWeight
              );
            if (apprMaxHPDualSteering !== undefined)
              newState.apprMaxHPDualSteering = this.choseApprovedValue(
                newState.calcMaxHPDualSteering,
                this.state.reqMaxHPDualSteering
              );
          }

          if (calBoatWeight !== undefined && data.calBoatWeight) newState.calBoatWeight = data.calBoatWeight;
          if (reqSpecifiedEngineWeight !== undefined && data.specifiedEngineWeight !== undefined) {
            newState.reqSpecifiedEngineWeight = data.specifiedEngineWeight > 0 ? data.specifiedEngineWeight : '';
          }
          if (data.useManufacturerEngineWeight !== undefined) {
            newState.useManufacturerEngineWeight = !!data.useManufacturerEngineWeight;
          }
          if (plateType !== undefined && data.plateType) {
            let plateType = data.plateType.replace(/_/g, ' ').toLowerCase();
            newState.plateType = plateType[0].toUpperCase() + plateType.slice(1, plateType.length);
          }

          this.setState(newState, () => {
            this.recalculationPending = false;
            resolve();
          });
        })

        .catch(() => {
          this.recalculationPending = false;
          reject();
        });
    });
  };

  isEmptyInputs = () => {
    const {dualSteering} = this.props.boat;
    let emptyInputs = false;

    Object.keys(this.state)
      .filter(stateFieldName => stateFieldName.includes('req'))
      .forEach(inputStateFieldName => {
        if (this.isOptionalField(inputStateFieldName)) return;
        if (!this.state[inputStateFieldName]) {
          if (dualSteering) emptyInputs = true;
          else if (!dualSteering && inputStateFieldName !== 'reqMaxHPDualSteering') emptyInputs = true;
        }
      });
    return emptyInputs;
  };

  isOptionalField = fieldName => fieldName === 'reqSpecifiedEngineWeight' && !this.isSpecifiedEngineWeightRequired();

  isElectricOutboard = () => {
    const {engineType, boatSpecifications} = this.props.boat;
    return engineType === OUTBOARD && !!(boatSpecifications && boatSpecifications.hasElectricPropulsion);
  };

  getBoatOverallLength = () => {
    const {boat} = this.props;

    if (boat.boatType === PONTOON) {
      const pontoonLengthInches = Number(boat.cubicCapacity && boat.cubicCapacity.calculatedPontoonLength);
      return isFinite(pontoonLengthInches) && pontoonLengthInches > 0 ? pontoonLengthInches / 12 : NaN;
    }

    const lengthFeet = Number(boat.boatSpecifications && boat.boatSpecifications.lengthFeet);
    const lengthInches = Number((boat.boatSpecifications && boat.boatSpecifications.lengthInches) || 0);
    return isFinite(lengthFeet) && lengthFeet > 0 ? lengthFeet + lengthInches / 12 : NaN;
  };

  shouldShowSpecifiedEngineWeight = () => {
    const {boatType, engineType} = this.props.boat;
    const overallLength = this.getBoatOverallLength();

    if (this.shouldShowTagTypeSelection()) {
      return engineType === OUTBOARD &&
        this.state.preferredTagType === 'MODERN' &&
        this.state.useManufacturerEngineWeight;
    }

    return engineType === OUTBOARD && (
      boatType === PONTOON ||
      (boatType === BOAT && isFinite(overallLength) && overallLength >= 20 && overallLength < 26)
    );
  };

  shouldShowTagTypeSelection = () => {
    const {boatType, engineType} = this.props.boat;
    const overallLength = this.getBoatOverallLength();

    return (boatType === BOAT || boatType === PONTOON) &&
      (engineType === OUTBOARD || engineType === INBOARD) &&
      isTagTypeSelectionLengthEligible(overallLength, boatType === PONTOON);
  };

  shouldShowEngineWeightSelection = () => this.shouldShowTagTypeSelection() &&
    this.props.boat.engineType === OUTBOARD &&
    this.state.preferredTagType === 'MODERN';

  isSpecifiedEngineWeightRequired = () => {
    const {dualSteering} = this.props.boat;

    if (this.shouldShowTagTypeSelection()) return this.shouldShowSpecifiedEngineWeight();

    return this.shouldShowSpecifiedEngineWeight() && (dualSteering || this.isElectricOutboard());
  };

  getSpecifiedEngineWeightSubLabel = () => {
    if (this.shouldShowTagTypeSelection()) return 'Required when using the manufacturer engine weight.';
    if (this.isSpecifiedEngineWeightRequired()) {
      return 'Required for boats with dual steering or electric propulsion.';
    }
    return 'Leave blank to continue calculating engine weight based on the S-30 table.';
  };

  getPersistedStepState = () => ({
    ...this.state,
    reqSpecifiedEngineWeight: this.shouldShowSpecifiedEngineWeight() ? this.state.reqSpecifiedEngineWeight : ''
  });

  recalculateAfterSelection = () => {
    const hasRequestedInputErrors = Object.keys(this.inputErrors)
      .some(stateFieldName => stateFieldName.includes('req'));

    if (!this.isEmptyInputs() && !hasRequestedInputErrors && !this.recalculationPending) {
      this.recalculationPending = true;
      this.calculateData().catch(() => {});
    }
  };

  selectTagType = preferredTagType => {
    if (this.isElectricOutboard()) return;

    const {markStepAsRedacted} = this.props;
    const updates = {preferredTagType};

    if (preferredTagType === 'LEGACY') {
      updates.useManufacturerEngineWeight = false;
      delete this.inputErrors.reqSpecifiedEngineWeight;
    }

    markStepAsRedacted();
    this.setState(updates, this.recalculateAfterSelection);
  };

  toggleTagType = () => {
    this.selectTagType(this.state.preferredTagType === 'MODERN' ? 'LEGACY' : 'MODERN');
  };

  toggleEngineWeightSource = () => {
    if (this.isElectricOutboard()) return;

    const {markStepAsRedacted} = this.props;
    const useManufacturerEngineWeight = !this.state.useManufacturerEngineWeight;

    if (!useManufacturerEngineWeight) delete this.inputErrors.reqSpecifiedEngineWeight;
    markStepAsRedacted();
    this.setState({useManufacturerEngineWeight}, () => {
      if (!useManufacturerEngineWeight) this.recalculateAfterSelection();
    });
  };

  validatePersonsWeight = () => {
    const {reqMaxNumberOfPersons, reqMaxPersonsWeight, overrideApprovedValues} = this.state;

    if (this.props.boat.boatType === PONTOON) {
      if (reqMaxNumberOfPersons !== undefined || reqMaxPersonsWeight !== undefined) {
        if (!overrideApprovedValues && (+reqMaxNumberOfPersons - 0.5) * 141 > +reqMaxPersonsWeight)
          this.inputErrors.reqMaxPersonsWeight = 'Persons Weight can\'t be less than (Persons * 141 Lbs.).';
        else if (this.inputErrors.reqMaxPersonsWeight === 'Persons Weight can\'t be less than (Persons * 141 Lbs.).')
          delete this.inputErrors.reqMaxPersonsWeight;
        this.forceUpdate();
      }
    } else if (reqMaxNumberOfPersons !== undefined || reqMaxPersonsWeight !== undefined) {
      if (!overrideApprovedValues && (+reqMaxNumberOfPersons - 0.5) * 141 - 32 > +reqMaxPersonsWeight)
        this.inputErrors.reqMaxPersonsWeight = 'Persons Weight can\'t be less than (Persons * 141 Lbs.) - 32 Lbs.';
      else if (this.inputErrors.reqMaxPersonsWeight === 'Persons Weight can\'t be less than (Persons * 141 Lbs.) - 32 Lbs.')
        delete this.inputErrors.reqMaxPersonsWeight;
      this.forceUpdate();
    }
  };

  validateMaximumWeight = () => {
    const {reqMaxWeight, reqMaxPersonsWeight} = this.state;

    if (reqMaxWeight !== undefined || reqMaxPersonsWeight !== undefined) {
      if (+reqMaxPersonsWeight > +reqMaxWeight) this.inputErrors.reqMaxWeight = 'Max Weight can\'t be less than Persons Weight';
      else if (this.inputErrors.reqMaxWeight === 'Max Weight can\'t be less than Persons Weight') delete this.inputErrors.reqMaxWeight;
      this.forceUpdate();
    }
  };

  validateStep = () => {
    return new Promise((resolve, reject) => {
      if (this.recalculationPending) {
        NotificationManager.error('note', 'Calculation in progress', 'Please wait, while system recalculate values');
        reject();
      } else {
        Object.keys(this.state)
          .filter((stateFieldName) => {
            return (!stateFieldName.includes('app') &&
              !stateFieldName.includes('App') &&
              stateFieldName !== 'plateType' &&
              stateFieldName !== 'preferredTagType' &&
              stateFieldName !== 'useManufacturerEngineWeight');
          })
          .forEach(field => {
            if (this.state[field] !== undefined && !this.isOptionalField(field) && (!this.state[field] || +this.state[field] <= 0)) {
              if (!this.inputErrors[field]) {
                if (field.includes('cal')) this.inputErrors[field] = 'The calculated values are invalid. Please verify input data';
                else this.inputErrors[field] = true;
              }
            } else delete this.inputErrors[field];
            if (field === 'reqMaxPersonsWeight') this.validatePersonsWeight();
            if (field === 'reqMaxWeight') this.validateMaximumWeight();
          });
        this.forceUpdate();
        if (Object.keys(this.inputErrors).length) reject(); else resolve();
      }
    });
  };

  handleInputChange = targetField => (value, error) => {
    const {markStepAsRedacted} = this.props;
    const isOptionalBlank = this.isOptionalField(targetField) && !value && !error;

    markStepAsRedacted();
    if ((!value && !isOptionalBlank) || error) {
      if (!this.inputErrors[targetField]) this.inputErrors[targetField] = true;
      this.setState({[targetField]: value});
    } else {
      if (this.inputErrors[targetField]) delete this.inputErrors[targetField];

      this.setState({[targetField]: value}, () => {
        if (targetField === 'reqMaxNumberOfPersons' || targetField === 'reqMaxPersonsWeight') this.validatePersonsWeight();
        if (targetField === 'reqMaxWeight' || targetField === 'reqMaxPersonsWeight') this.validateMaximumWeight();
        if (targetField === 'reqSpecifiedEngineWeight') return;
        if (!this.isEmptyInputs() &&
          !Object.keys(this.inputErrors).filter(stateFieldName => stateFieldName.includes('req')).length) {
          if (!this.recalculationPending) {
            this.recalculationPending = true;

            if (this.timerIndex) clearTimeout(this.timerIndex);

            this.timerIndex = setTimeout(() => {
              this.calculateData();
            }, 1000);
          }
        }
      });
    }
  };

  handleSpecifiedEngineWeightBlur = () => {
    this.recalculateAfterSelection();
  };

  choseApprovedValue = (calc, req) => {
    const {overrideApprovedValues} = this.state;

    if (overrideApprovedValues || !isFinite(calc)) return req;

    return (calc < req ? calc : req);
  };

  toggleOverride = () => {
    const {markStepAsRedacted} = this.props;
    const {
      calMaxNumberOfPersons, calMaxPersonsWeight, calMaxWeight, calcMaxHP, calcMaxHPDualSteering,
      reqMaxNumberOfPersons, reqMaxPersonsWeight, reqMaxWeight, reqMaxHP, reqMaxHPDualSteering,
      appMaxNumberOfPersons, appMaxPersonsWeight, appMaxWeight, apprMaxHP, apprMaxHPDualSteering,
      overrideApprovedValues
    } = this.state;
    if(this.inputErrors.reqMaxPersonsWeight)
      delete this.inputErrors.reqMaxPersonsWeight;

    markStepAsRedacted();

    this.setState({overrideApprovedValues: !overrideApprovedValues}, () => {
      let newState = {};

      if (apprMaxHP !== undefined) newState.apprMaxHP = this.choseApprovedValue(calcMaxHP, reqMaxHP);
      if (appMaxWeight !== undefined) newState.appMaxWeight = this.choseApprovedValue(calMaxWeight, reqMaxWeight);
      if (appMaxNumberOfPersons !== undefined) newState.appMaxNumberOfPersons = this.choseApprovedValue(calMaxNumberOfPersons, reqMaxNumberOfPersons);
      if (appMaxPersonsWeight !== undefined) newState.appMaxPersonsWeight = this.choseApprovedValue(calMaxPersonsWeight, reqMaxPersonsWeight);
      if (apprMaxHPDualSteering !== undefined) newState.apprMaxHPDualSteering = this.choseApprovedValue(calcMaxHPDualSteering, reqMaxHPDualSteering);

      this.setState(newState)
    })

  };

  handleMoveToNextStep = () => {
    const {updateBoatDetails, goToStep} = this.props;

    this.validateStep()
      .then(() => {
        this.calculateData()
          .then(() => {
            updateBoatDetails({personPowerCapacity: this.getPersistedStepState()});
            goToStep('next', true);
          });
      });
  };

  handleMoveToArbitraryStep = stepId => {
    const {updateBoatDetails, goToStep, stepIsChanged} = this.props;

    if (!stepIsChanged) {
      return goToStep(typeof stepId === 'string' ? stepId : 'prev', true);
    }

    this.validateStep()
      .then(() => {
        updateBoatDetails({personPowerCapacity: this.getPersistedStepState()});
        this.calculateData()
          .then(() => {
            goToStep(typeof stepId === 'string' ? stepId : 'prev', true);
          })
          .catch(() => {
            goToStep(typeof stepId === 'string' ? stepId : 'prev', false);
          });
      })
      .catch(() => {
        let newState = this.state;
        for (let fieldName in this.state) {
          if (this.inputErrors[fieldName]) {
            newState[fieldName] = '';
          }
        }
        this.setState(newState, () => {
          updateBoatDetails({personPowerCapacity: this.getPersistedStepState()});
          goToStep(typeof stepId === 'string' ? stepId : 'prev', false);
        });
      });
  };

  render() {
    const {
      calMaxNumberOfPersons, calMaxPersonsWeight, calMaxWeight, calcMaxHP, calcMaxHPDualSteering,
      reqMaxNumberOfPersons, reqMaxPersonsWeight, reqMaxWeight, reqMaxHP, reqMaxHPDualSteering,
      appMaxNumberOfPersons, appMaxPersonsWeight, appMaxWeight, apprMaxHP, apprMaxHPDualSteering,
      calBoatWeight, plateType, overrideApprovedValues, reqSpecifiedEngineWeight,
      preferredTagType, useManufacturerEngineWeight
    } = this.state;
    const {user, boat, getNavBar, getHeader, stepIsChanged, updateBoatDetails} = this.props;
    const showSpecifiedEngineWeight = this.shouldShowSpecifiedEngineWeight();
    const electricOutboard = this.isElectricOutboard();
    const hasCalculatedValues = (
      calMaxNumberOfPersons !== undefined ||
      calMaxPersonsWeight !== undefined ||
      calMaxWeight !== undefined ||
      calcMaxHP !== undefined ||
      calcMaxHPDualSteering !== undefined
    );

    return (
      <React.Fragment>
        {getHeader(() => {
          return new Promise(resolve => {
            this.validateStep()
              .then(() => {
                this.calculateData();
                updateBoatDetails({ personPowerCapacity: this.getPersistedStepState() });
                resolve();
              })
              .catch(() => {});
          });
        })}

        {getNavBar(this.handleMoveToNextStep, this.handleMoveToArbitraryStep)}

        {(calBoatWeight !== undefined || plateType !== undefined) && (
          <section className='scene_section person-weight-capacities-section _border_top_1'>
            <header className='_margin_bottom_20'>
              {`${calBoatWeight !== undefined && 'Boat Weight'}${
                calBoatWeight !== undefined && plateType !== undefined && ' / '
              }${plateType !== undefined && 'Plate Type'}`}
            </header>
            {calBoatWeight !== undefined && (
              <CalculatedOutput
                mainWrapperClassName='_margin_right_30'
                label='Calculated Weight'
                color='gray'
                value={calBoatWeight || ''}
                units='Lbs.'
              />
            )}
            {plateType !== undefined && (
              <CalculatedOutput label='Plate Type' color='gray' removeUnderlines value={plateType} />
            )}
          </section>
        )}

        <section className='scene_section person-weight-capacities-section'>
          <header>Requested Person/ Weight Capacities</header>

          {this.shouldShowTagTypeSelection() && (
            <div className='capacity-tag-preferences'>
              <div className='capacity-tag-preference-row'>
                <button
                  type='button'
                  className={`capacity-tag-image${preferredTagType === 'LEGACY' ? ' capacity-tag-image_selected' : ''}`}
                  onClick={() => this.selectTagType('LEGACY')}
                  disabled={electricOutboard}
                  aria-label='Select Legacy capacity tag'
                  aria-pressed={preferredTagType === 'LEGACY'}
                >
                  <img src={legacyTagImage} alt='Legacy capacity tag preview' />
                </button>
                <Slider
                  state={preferredTagType !== 'MODERN'}
                  mainWrapperClassName='green'
                  onLabel='Legacy'
                  offLabel='New'
                  toggleAction={this.toggleTagType}
                  disabled={electricOutboard}
                />
                <button
                  type='button'
                  className={`capacity-tag-image${preferredTagType === 'MODERN' ? ' capacity-tag-image_selected' : ''}`}
                  onClick={() => this.selectTagType('MODERN')}
                  disabled={electricOutboard}
                  aria-label='Select Modern capacity tag'
                  aria-pressed={preferredTagType === 'MODERN'}
                >
                  <img src={modernTagImage} alt='Modern capacity tag preview' />
                </button>
              </div>

              {this.shouldShowEngineWeightSelection() && (
                <div className='capacity-tag-engine-weight-selector'>
                  <Slider
                    state={!useManufacturerEngineWeight}
                    mainWrapperClassName='green'
                    label='Engine Weight Source'
                    onLabel='S-30 Engine Weight'
                    offLabel='MFR Engine Weight'
                    toggleAction={this.toggleEngineWeightSource}
                    disabled={electricOutboard}
                  />
                </div>
              )}
            </div>
          )}

          {hasCalculatedValues ? (
            <p className='reminder'>
              You can use request values to change approved values.
              <br />
              The input values could not be more than maximum calculated.
            </p>
          ) : (
            <p className='reminder'>You can use request values to change approved values.</p>
          )}

          {(reqMaxNumberOfPersons !== undefined || reqMaxPersonsWeight !== undefined) && (
            <div className='multi-input-fields-wrapper _margin_right_30'>
              {reqMaxNumberOfPersons !== undefined && (
                <MultiInput
                  label='Maximum Persons *'
                  value={reqMaxNumberOfPersons || ''}
                  onChange={this.handleInputChange('reqMaxNumberOfPersons')}
                  styles={{ height: 40 }}
                  error={this.inputErrors['reqMaxNumberOfPersons']}
                  isNumeric
                />
              )}
              {reqMaxPersonsWeight !== undefined && (
                <MultiInput
                  label={`Maximum ${boat.boatType !== PWC ? 'Persons ' : ''}Weight *`}
                  value={reqMaxPersonsWeight || ''}
                  onChange={this.handleInputChange('reqMaxPersonsWeight')}
                  styles={{ height: 40 }}
                  error={this.inputErrors['reqMaxPersonsWeight']}
                  units='Lbs.'
                  isNumeric
                />
              )}
            </div>
          )}
          {reqMaxWeight !== undefined && (
            <Input
              isNumeric
              mainWrapperClassName='input__main-wrapper_primary'
              inputClassName='input__input-field_primary'
              label='Maximum Weight *'
              value={reqMaxWeight || ''}
              units='Lbs.'
              error={this.inputErrors['reqMaxWeight']}
              onChange={this.handleInputChange('reqMaxWeight')}
            />
          )}
          {reqMaxHP !== undefined && (
            <Input
              isNumeric
              mainWrapperClassName='input__main-wrapper_primary _margin_right_30'
              inputClassName='input__input-field_primary'
              label='Maximum Horsepower *'
              value={reqMaxHP || ''}
              units='HP'
              onChange={this.handleInputChange('reqMaxHP')}
              error={this.inputErrors['reqMaxHP']}
            />
          )}
          {reqMaxHPDualSteering !== undefined && (
            <Input
              isNumeric
              mainWrapperClassName='input__main-wrapper_primary'
              inputClassName='input__input-field_primary'
              label='Maximum Horsepower without Remote Steering *'
              value={reqMaxHPDualSteering || ''}
              units='HP'
              onChange={this.handleInputChange('reqMaxHPDualSteering')}
              error={this.inputErrors['reqMaxHPDualSteering']}
            />
          )}
          {showSpecifiedEngineWeight && (
            <Input
              isNumeric
              mainWrapperClassName='ect-full-input'
              inputClassName='input__input-field_primary'
              label={`Outboard Engine Running Weight - Including prop shaft, prop, and fluids (Do not include Controls, Rigging, Battery and Portable Fuel Weight)${this.isSpecifiedEngineWeightRequired() ? ' *' : ''}`}
              subLabel={this.getSpecifiedEngineWeightSubLabel()}
              secondarySubLabel={boat.boatSpecifications && boat.boatSpecifications.motorType === TWIN
                ? 'Twin Engines - Enter weight for the combined engines.'
                : ''}
              subLabelIconClassName='input__sub-label-icon_attention'
              value={reqSpecifiedEngineWeight || ''}
              units='Lbs.'
              onChange={this.handleInputChange('reqSpecifiedEngineWeight')}
              onBlur={this.handleSpecifiedEngineWeightBlur}
              error={this.inputErrors['reqSpecifiedEngineWeight']}
            />
          )}
        </section>

        <section
          className={`scene_section scene_section_two-columns${
            user.isInternal() ? '_uneven' : ''
          } person-weight-capacities-section`}
        >
          <header className='_margin_bottom_20'>Calculated and Approved Person/ Weight Capacities</header>
          {user.isInternal() && boat.boatType !== PWC && (
            <div className='_width_pc_100 _flex _ai_center _jc_start _margin_bottom_20 _padding_left_4'>
              <Slider
                state={!overrideApprovedValues}
                mainWrapperClassName='green'
                label='Override with requested'
                onLabel='Off'
                offLabel='On'
                toggleAction={this.toggleOverride}
              />
            </div>
          )}
          {(calMaxNumberOfPersons !== undefined || calMaxPersonsWeight !== undefined) && (
            <div className='multi-output-fields-wrapper'>
              {calMaxNumberOfPersons !== undefined && (
                <CalculatedMultiOutput
                  label='Calculated Maximum Persons'
                  color='gray'
                  value={calMaxNumberOfPersons}
                  error={
                    this.inputErrors['calMaxPersonsWeight']
                      ? !!this.inputErrors['calMaxNumberOfPersons']
                      : this.inputErrors['calMaxNumberOfPersons']
                  }
                />
              )}
              {calMaxPersonsWeight !== undefined && (
                <CalculatedMultiOutput
                  label='Maximum Persons Weight'
                  color='gray'
                  value={calMaxPersonsWeight}
                  error={this.inputErrors['calMaxPersonsWeight']}
                  units='Lbs.'
                />
              )}
            </div>
          )}
          {(appMaxNumberOfPersons !== undefined || appMaxPersonsWeight !== undefined) && (
            <div className='multi-output-fields-wrapper'>
              {appMaxNumberOfPersons !== undefined && (
                <CalculatedMultiOutput
                  label='Approved Maximum Persons'
                  color='green'
                  value={appMaxNumberOfPersons}
                  error={this.inputErrors['appMaxNumberOfPersons']}
                />
              )}
              {appMaxPersonsWeight !== undefined && (
                <CalculatedMultiOutput
                  label='Maximum Persons Weight'
                  color='green'
                  value={appMaxPersonsWeight}
                  error={this.inputErrors['appMaxPersonsWeight']}
                  units='Lbs.'
                />
              )}
            </div>
          )}
          {calMaxWeight !== undefined && (
            <CalculatedOutput
              label='Calculated Maximum Weight'
              color='gray'
              value={calMaxWeight}
              error={this.inputErrors['calMaxWeight']}
              units='Lbs.'
            />
          )}
          {appMaxWeight !== undefined && (
            <CalculatedOutput
              label='Approved Maximum Weight'
              value={appMaxWeight}
              error={this.inputErrors['appMaxWeight']}
              units='Lbs.'
            />
          )}
          {calcMaxHP !== undefined && (
            <CalculatedOutput
              label='Calculated Maximum Horsepower'
              color='gray'
              value={calcMaxHP}
              error={this.inputErrors['calcMaxHP']}
              units='HP'
            />
          )}
          {apprMaxHP !== undefined && (
            <CalculatedOutput
              label='Approved Maximum Horsepower'
              value={apprMaxHP}
              error={this.inputErrors['apprMaxHP']}
              units='HP'
            />
          )}
          {calcMaxHPDualSteering !== undefined && (
            <CalculatedOutput
              label='Calculated Maximum Horsepower without Remote Steering'
              color='gray'
              value={calcMaxHPDualSteering}
              error={this.inputErrors['calcMaxHPDualSteering']}
              units='HP'
            />
          )}
          {apprMaxHPDualSteering !== undefined && (
            <CalculatedOutput
              label='Approved Maximum Horsepower without remote Steering'
              value={apprMaxHPDualSteering}
              error={this.inputErrors['apprMaxHPDualSteering']}
              units='HP'
            />
          )}
        </section>

        <ApplicationActions
          backButtonLabel='Prev. Step'
          saveButtonLabel='Next Step'
          saveButtonAction={
            stepIsChanged
              ? this.handleMoveToNextStep
              : () => {
                this.handleMoveToArbitraryStep('next');
              }
          }
          backButtonAction={this.handleMoveToArbitraryStep}
        />
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/Scenes/BoatManufacturer/Application/New/PersonPowerCapacity.jsx